import TosVersion_Entity from "./tosVersion";

/**
 * Notice we owe customers before a new version binds them. Mirrors the period
 * stated in the Terms themselves — shortening one without the other is a breach
 * of the contract we publish.
 */
export const NOTICE_PERIOD_DAYS = 30;

export default class TosVersion extends TosVersion_Entity {
  static fromList(tosVersionsJSON: unknown): Array<TosVersion> {
    const tosVersions: TosVersion[] = [];
    if (tosVersionsJSON)
      Array.isArray(tosVersionsJSON) &&
        tosVersionsJSON.forEach((tosVersionJSON) => {
          tosVersions.push(new TosVersion(tosVersionJSON));
        });
    return tosVersions;
  }

  toJson(): string {
    return JSON.stringify(this);
  }
}

/**
 * Days between now and when a version starts binding. Negative once effective.
 *
 * @param effectiveAt
 * @param now
 */
export function daysUntilEffective(
  effectiveAt: string | undefined,
  now: Date = new Date()
): number | undefined {
  if (!effectiveAt) return undefined;
  const t = new Date(effectiveAt).getTime();
  if (isNaN(t)) return undefined;
  return Math.ceil((t - now.getTime()) / 86400000);
}

/**
 * Whether a version still gives customers the notice the Terms promise. Surfaced
 * as a warning rather than enforced: a genuinely urgent security or legal change
 * may have to go out sooner, but it should be a deliberate choice.
 *
 * @param effectiveAt
 * @param now
 */
export function givesFullNotice(
  effectiveAt: string | undefined,
  now: Date = new Date()
): boolean {
  const days = daysUntilEffective(effectiveAt, now);
  if (days === undefined) return false;
  return days >= NOTICE_PERIOD_DAYS;
}

/**
 * Human labels for the document kinds, and whether each is a contract.
 *
 * The Privacy Policy is a notice rather than an agreement — there is nothing to
 * agree to in a description of what we do with data — so it publishes as
 * notice-only and never asks anybody to accept it.
 */
export const TOS_KINDS: Array<{
  value: "terms" | "privacy" | "payroll";
  label: string;
  requiresAcceptance: boolean;
  hint: string;
}> = [
  {
    value: "terms",
    label: "Terms and Conditions",
    requiresAcceptance: true,
    hint: "Applies to every organization.",
  },
  {
    value: "privacy",
    label: "Privacy Policy",
    requiresAcceptance: false,
    hint: "A notice, not a contract — shown but never accepted.",
  },
  {
    value: "payroll",
    label: "Payroll Services Addendum",
    requiresAcceptance: true,
    hint: "Opt-in — scope it to the organizations using Rhapsody Payroll.",
  },
];

/**
 * @param kind
 */
export function tosKindLabel(kind: string | undefined): string {
  return (
    TOS_KINDS.find((k) => k.value === kind)?.label ?? "Terms and Conditions"
  );
}
